import React, { useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Label, Segment } from 'semantic-ui-react'
import { GeneralService } from '~/api/services/general'
import FileUpload from '~/components/FileUpload'
import { FormFields } from '~/components/FormFields'
import notify from '~/components/Notify'
import { StyledFileWrapper } from '../../style'
import { b64toBlob, handleErrors } from '~/utils/helpers'
import Dialog from '~/components/Dialog'

interface LanguageFormProps {
  index: number
  indexOfTerms: number
  types: any[]
}

const LanguageForm: React.FunctionComponent<LanguageFormProps> = (props) => {
  const { control, setValue, getValues, formState } = useFormContext()
  const { t } = useTranslation()
  const [isBusy, setIsBusy] = useState(false)
  const [fileSource, setFileSource] = useState(null)
  const { index, indexOfTerms, types } = props
  const watchFileType = useWatch({ control: control, name: `documents[${indexOfTerms}].languages[${index}].fileType` })
  const watchFile = useWatch({ control: control, name: `documents[${indexOfTerms}].languages[${index}].file` })
  const watchFileName = useWatch({ control: control, name: `documents[${indexOfTerms}].languages[${index}].originalFilename` })
  const validationOn = useWatch({ control, name: `documents[${indexOfTerms}].status` }) > 0

  async function handleDownloadPdf() {
    setIsBusy(true);
    try {
      if (watchFile) {
        setFileSource(watchFile)
      } else {
        const fileName = getValues(`documents[${indexOfTerms}].languages[${index}].filename`)
        const response = await GeneralService.getFile({ fileName: fileName });
        const type = response.data.split(';')[0].split('data:')[1]
        const blob = b64toBlob(response.data, type);
        const blobUrl = URL.createObjectURL(blob);
        setFileSource(blobUrl);
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderFileField() {
    return <FormFields.External
      control={control}
      name={`documents[${indexOfTerms}].languages[${index}].file`}
      width={4}
      label={t('File')}
      onChange={(value, name) => {
        setValue(`documents[${indexOfTerms}].languages[${index}].file`, value);
        setValue(`documents[${indexOfTerms}].languages[${index}].originalFilename`, name);
      }}
    >
      <FileUpload
        button
        encodeFiles
        accept={{
          'application/pdf': ['.pdf']
        }}
        sizeLimitInMB={10}
      />
    </FormFields.External>
  }

  useEffect(() => {
    if (watchFileType === 'link') {
      setValue(`documents[${indexOfTerms}].languages[${index}].file`, null);
      setValue(`documents[${indexOfTerms}].languages[${index}].originalFilename`, null);
    }
    else if (watchFileType === 'file') {
      setValue(`documents[${indexOfTerms}].languages[${index}].link`, null)
    }
  }, [watchFileType])

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
    />
  }

  function renderLinkField() {
    return <FormFields.Input
      label={t('Link')}
      name={`documents[${indexOfTerms}].languages[${index}].link`}
      control={control}
      rules={{
        required: validationOn && watchFileType === 'link' ? t('Please enter link') : false
      }}
      placeholder={t('Link')}
    />
  }

  function renderFile() {
    return <StyledFileWrapper>
      <Label
        basic
        onClick={() => handleDownloadPdf()}
        color='blue'
        as='a'
        content={watchFileName}
        icon='file'
      />
    </StyledFileWrapper>
  }

  return <>
    <FormFields.Input
      control={control}
      name={`documents[${indexOfTerms}].languages[${index}].name`}
      label={t('Name')}
      rules={{
        required: validationOn ? t('Please enter name') : false
      }}
      placeholder={t('Name')}
    />
    <FormFields.Select
      label={t('Type')}
      control={control}
      name={t(`documents[${indexOfTerms}].languages[${index}].fileType`)}
      selectOnBlur={false}
      placeholder={t('Select type')}
      selection
      rules={{
        required: validationOn ? t('Please select type') : false
      }}
      options={types}
    />
    {fileSource && renderModalPreviewFile()}
    <Segment loading={isBusy} style={{ position: 'relative' }}>
      {watchFileName && renderFile()}
      {watchFileType === 'link' ? renderLinkField() : renderFileField()}
    </Segment>
  </>

}
export default LanguageForm
